import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

/**
 * Cross-platform secure storage utility
 * Handles the differences between mobile (SecureStore) and web (localStorage)
 */

export class SecureStorage {
  /**
   * Store a value securely
   */
  static async setItem(key: string, value: string): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        // Web platform - use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.setItem(key, value);
          console.log(`✅ Storage: Set ${key} in localStorage (web)`);
        } else {
          throw new Error('localStorage not available');
        }
      } else {
        // Mobile platform - use SecureStore
        await SecureStore.setItemAsync(key, value);
        console.log(`✅ Storage: Set ${key} in SecureStore (mobile)`);
      }
    } catch (error) {
      console.error(`❌ Storage: Failed to set ${key}:`, error);
      throw error;
    }
  }

  /**
   * Get a value from secure storage
   */
  static async getItem(key: string): Promise<string | null> {
    try {
      if (Platform.OS === 'web') {
        // Web platform - use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          const value = window.localStorage.getItem(key);
          console.log(`📖 Storage: Retrieved ${key} from localStorage (web)`, { hasValue: !!value });
          return value;
        } else {
          console.log('📖 Storage: localStorage not available (web)');
          return null;
        }
      } else {
        // Mobile platform - use SecureStore
        const value = await SecureStore.getItemAsync(key);
        console.log(`📖 Storage: Retrieved ${key} from SecureStore (mobile)`, { hasValue: !!value });
        return value;
      }
    } catch (error) {
      console.error(`❌ Storage: Failed to get ${key}:`, error);
      return null;
    }
  }

  /**
   * Remove a value from secure storage
   */
  static async removeItem(key: string): Promise<void> {
    try {
      if (Platform.OS === 'web') {
        // Web platform - use localStorage
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem(key);
          console.log(`🗑️ Storage: Removed ${key} from localStorage (web)`);
        }
      } else {
        // Mobile platform - use SecureStore
        await SecureStore.deleteItemAsync(key);
        console.log(`🗑️ Storage: Removed ${key} from SecureStore (mobile)`);
      }
    } catch (error) {
      console.error(`❌ Storage: Failed to remove ${key}:`, error);
      // Don't throw error for removal failures - it's often not critical
    }
  }

  /**
   * Clear all auth-related storage
   */
  static async clearAuthStorage(): Promise<void> {
    const authKeys = [
      'auth_token',
      'verification_token',
      'post_login_redirect',
      'refresh_token', // for future use
    ];

    await Promise.all(
      authKeys.map(key => this.removeItem(key).catch(error => {
        console.warn(`⚠️ Storage: Failed to clear ${key}:`, error);
      }))
    );

    console.log('🧹 Storage: Auth storage cleared');
  }

  /**
   * Check if storage is available
   */
  static isStorageAvailable(): boolean {
    if (Platform.OS === 'web') {
      return typeof window !== 'undefined' && !!window.localStorage;
    }
    return true; // SecureStore is always available on mobile
  }
}

/**
 * Utility functions for managing redirect URLs
 */
export class RedirectManager {
  private static readonly REDIRECT_KEY = 'post_login_redirect';

  /**
   * Store a URL to redirect to after login
   */
  static async storeRedirectUrl(url: string): Promise<void> {
    await SecureStorage.setItem(this.REDIRECT_KEY, url);
    console.log('🔗 RedirectManager: Stored redirect URL:', url);
  }

  /**
   * Get and clear the stored redirect URL
   */
  static async getAndClearRedirectUrl(): Promise<string | null> {
    const url = await SecureStorage.getItem(this.REDIRECT_KEY);
    if (url) {
      await SecureStorage.removeItem(this.REDIRECT_KEY);
      console.log('🔗 RedirectManager: Retrieved and cleared redirect URL:', url);
    }
    return url;
  }

  /**
   * Clear any stored redirect URL
   */
  static async clearRedirectUrl(): Promise<void> {
    await SecureStorage.removeItem(this.REDIRECT_KEY);
    console.log('🔗 RedirectManager: Cleared redirect URL');
  }

  /**
   * Get current URL (web only)
   */
  static getCurrentUrl(): string | null {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      return window.location.href;
    }
    return null;
  }

  /**
   * Redirect to URL (web only)
   */
  static redirectToUrl(url: string): void {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      console.log('🔗 RedirectManager: Redirecting to:', url);
      window.location.href = url;
    } else {
      console.warn('🔗 RedirectManager: URL redirect not available on this platform');
    }
  }
}
